import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../../components/Input'
import { Textarea } from '../../components/Textarea'
import { FormNavigation } from '../../components/FormNavigation'
import { useOnboardingStore } from '../../store/onboardingStore'
import { useTestMode } from '../../context/TestModeContext'
import { documentService } from '../../services/api'
import { reportUploadError } from '../../services/errorReporter'
import { MAX_UPLOAD_BYTES, formatFileSize } from '../../utils/fileLimits'
import { isValidItalianTaxCode, isValidEmail, validatePhoneNumber } from '../../utils/validation'
import { isMinor, SINGLE_SIGNATURE_REASONS } from '../../utils/minor'
import { toast } from 'sonner'
import { Upload, X, FileText, Info } from 'lucide-react'
import { logger } from '../../utils/logger'
import type { StepProps } from '../../types'

// Guardian types exactly as in the reference dropdown (change request §4.4).
// Proper Italian terms, shown as-is in every UI language.
const GUARDIAN_TYPES = [
  'Genitore',
  'Tutore',
  'Azienda/Società',
  'Coniuge/Partner',
  'Altro',
  'Genitore affidatario',
  'Curatore',
  'Amministratore di sostegno',
]

// A minor's consent is signed by the parents ("genitori esercenti la responsabilità genitoriale")
// or by the Tutore — the signature lines of the minors consent (Arne, 2026-10-26)
const MINOR_GUARDIAN_TYPES = ['Genitore', 'Tutore']

/**
 * Italy replaces the insurance step with "Tutor legale" (change request
 * 2026-08-25, §4): Sì/No, then guardian type, then the guardian's anagraphic
 * data following the structure of the patient's own demographic step.
 * DE/ES keep Step4Insurance untouched.
 *
 * Patient under 18 (birth date from step 2): no Sì/No — the parents' data is required, because
 * both parents sign the minors consent, each with an SMS code on their own mobile. Instead of the
 * other parent the family can state why one signature is enough (Arne's three cases). A Tutore
 * signs alone.
 */
export function ItalyGuardianStep({ onNext, onBack }: StepProps) {
  const { t } = useTranslation()
  const isTestMode = useTestMode()
  const { guardianData, setGuardianData, patientNumber, personalData } = useOnboardingStore()
  const [isUploading, setIsUploading] = useState(false)
  const [taxCodeError, setTaxCodeError] = useState<string | undefined>()
  const [emailError, setEmailError] = useState<string | undefined>()
  const [phoneError, setPhoneError] = useState<string | undefined>()
  const [phone2Error, setPhone2Error] = useState<string | undefined>()
  const [email2Error, setEmail2Error] = useState<string | undefined>()

  const minor = isMinor(personalData.birthDate)
  const guardianTypes = minor ? MINOR_GUARDIAN_TYPES : GUARDIAN_TYPES

  // A minor always has a legal representative; an adult-only type chosen earlier no longer fits
  useEffect(() => {
    if (!minor) return
    const patch: Partial<typeof guardianData> = {}
    if (!guardianData.hasGuardian) patch.hasGuardian = true
    if (guardianData.guardianType && !MINOR_GUARDIAN_TYPES.includes(guardianData.guardianType)) patch.guardianType = ''
    if (Object.keys(patch).length > 0) setGuardianData(patch)
  }, [minor, guardianData.hasGuardian, guardianData.guardianType, setGuardianData])

  const has = minor || guardianData.hasGuardian

  // Mirror first+last into the legacy guardianName column so webhooks and the
  // admin views keep working unchanged.
  const setNames = (patch: { guardianFirstName?: string; guardianLastName?: string }) => {
    const first = patch.guardianFirstName ?? guardianData.guardianFirstName ?? ''
    const last = patch.guardianLastName ?? guardianData.guardianLastName ?? ''
    setGuardianData({ ...patch, guardianName: [first, last].filter(Boolean).join(' ') })
  }

  const taxCode = (guardianData.guardianTaxCode || '').trim()
  const email = (guardianData.guardianEmail || '').trim()
  const phone = (guardianData.guardianPhone || '').trim()
  const phone2 = (guardianData.guardian2Phone || '').trim()
  const email2 = (guardianData.guardian2Email || '').trim()
  const name = (v?: string) => (v || '').trim().length >= 2
  const phoneOk = (v: string) => !!v && validatePhoneNumber(v).valid

  // Minor with a parent (not a Tutore): the other parent signs too, unless one of Arne's cases applies
  const needsSignatureChoice = minor && guardianData.guardianType === 'Genitore'
  const signaturesValid =
    !needsSignatureChoice ||
    (guardianData.guardianSignatures === 'both' &&
      name(guardianData.guardian2FirstName) &&
      name(guardianData.guardian2LastName) &&
      phoneOk(phone2) &&
      (!email2 || isValidEmail(email2))) ||
    (guardianData.guardianSignatures === 'single' &&
      (SINGLE_SIGNATURE_REASONS as readonly string[]).includes(guardianData.guardianSingleSignatureReason || ''))

  const isValid =
    !has ||
    (!!guardianData.guardianType &&
      guardianTypes.includes(guardianData.guardianType) &&
      name(guardianData.guardianFirstName) &&
      name(guardianData.guardianLastName) &&
      // Birth data stays required for an adult's guardian; for a minor's parents the mobile is what counts
      (minor || (!!guardianData.guardianBirthDate && (guardianData.guardianBirthPlace || '').trim().length >= 2)) &&
      (!minor || phoneOk(phone)) &&
      (!taxCode || isValidItalianTaxCode(taxCode)) &&
      (!email || isValidEmail(email)) &&
      signaturesValid)

  const phoneMessage = (v: string) => (!v ? t('guardianStep.mobileRequired') : validatePhoneNumber(v).error)

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      toast.error(t('common.onlyPdfAllowed'))
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(t('common.fileTooLarge', { size: formatFileSize(file.size), max: formatFileSize(MAX_UPLOAD_BYTES) }))
      return
    }
    if (isTestMode) {
      toast.success(t('common.testModeUploadSkipped'))
      setGuardianData({ guardianDocumentFilename: `TESTMODE_${file.name}` })
      return
    }
    setIsUploading(true)
    try {
      const response = await documentService.uploadPdf(file, patientNumber, 'guardian')
      if (response.success && response.data) {
        setGuardianData({ guardianDocumentFilename: response.data.filename })
      }
    } catch (error) {
      logger.error('Upload failed:', error)
      reportUploadError(error, { patientNumber, step: 'guardian', filename: file.name })
      toast.error(t('common.uploadFailed'))
    } finally {
      setIsUploading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isValid) onNext()
  }

  const yesNo = (value: boolean, label: string) => (
    <button
      type="button"
      onClick={() => setGuardianData({ hasGuardian: value })}
      className={`p-4 rounded-lg border-2 text-left transition-all ${
        has === value ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
            has === value ? 'border-primary' : 'border-gray-300'
          }`}
        >
          {has === value && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
        </div>
        <span className="font-medium">{label}</span>
      </div>
    </button>
  )

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <h2 className="text-h2 text-foreground mb-2">{t(minor ? 'guardianStep.minorTitle' : 'guardianStep.title')}</h2>
        {minor ? (
          <div className="flex gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden />
            <p className="text-body text-foreground">{t('guardianStep.minorDescription')}</p>
          </div>
        ) : (
          <>
            <p className="text-body text-muted-foreground mb-8">{t('guardianStep.description')}</p>
            <div className="grid grid-cols-2 gap-4">
              {yesNo(false, t('common.no'))}
              {yesNo(true, t('common.yes'))}
            </div>
          </>
        )}

        {has && (
          <div className="space-y-4 mt-8">
            {/* Guardian type first, before any personal data (§4.4) */}
            <div>
              <label htmlFor="guardianType" className="block text-body font-medium text-foreground mb-1.5">
                {t('guardianStep.typeLabel')} *
              </label>
              <select
                id="guardianType"
                value={guardianData.guardianType || ''}
                onChange={(e) => setGuardianData({ guardianType: e.target.value })}
                className="w-full px-4 py-3 text-body border border-border rounded-input outline-none bg-white focus:border-primary"
              >
                <option value="">{t('guardianStep.typePlaceholder')}</option>
                {guardianTypes.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </div>

            {guardianData.guardianType && (
              <>
                <Input
                  id="guardianFirstName"
                  type="text"
                  label={`${t('step2.firstName')} *`}
                  value={guardianData.guardianFirstName || ''}
                  onChange={(e) => setNames({ guardianFirstName: e.target.value })}
                />
                <Input
                  id="guardianLastName"
                  type="text"
                  label={`${t('step2.lastName')} *`}
                  value={guardianData.guardianLastName || ''}
                  onChange={(e) => setNames({ guardianLastName: e.target.value })}
                />
                <Input
                  id="guardianBirthDate"
                  type="date"
                  label={minor ? t('step2.birthDate') : `${t('step2.birthDate')} *`}
                  value={guardianData.guardianBirthDate || ''}
                  onChange={(e) => setGuardianData({ guardianBirthDate: e.target.value })}
                />
                <Input
                  id="guardianBirthPlace"
                  type="text"
                  label={minor ? t('step2.birthPlace') : `${t('step2.birthPlace')} *`}
                  value={guardianData.guardianBirthPlace || ''}
                  onChange={(e) => setGuardianData({ guardianBirthPlace: e.target.value })}
                />
                <Input
                  id="guardianTaxCode"
                  type="text"
                  label={t('step2.codiceFiscale')}
                  placeholder={t('step2.codiceFiscalePlaceholder')}
                  value={guardianData.guardianTaxCode || ''}
                  onChange={(e) => {
                    setGuardianData({ guardianTaxCode: e.target.value.toUpperCase() })
                    if (taxCodeError) setTaxCodeError(undefined)
                  }}
                  onBlur={() => {
                    setTaxCodeError(
                      !taxCode || isValidItalianTaxCode(taxCode) ? undefined : t('step2.codiceFiscaleInvalid')
                    )
                  }}
                  error={taxCodeError}
                />
                <div>
                  <Input
                    id="guardianPhone"
                    type="tel"
                    label={minor ? `${t('guardianStep.mobileLabel')} *` : t('step2.phone')}
                    placeholder={t('step2.phonePlaceholder')}
                    value={guardianData.guardianPhone || ''}
                    onChange={(e) => {
                      setGuardianData({ guardianPhone: e.target.value })
                      if (phoneError) setPhoneError(undefined)
                    }}
                    onBlur={() => { if (minor) setPhoneError(phoneOk(phone) ? undefined : phoneMessage(phone)) }}
                    error={phoneError}
                  />
                  {minor && <p className="text-small text-muted-foreground mt-1">{t('guardianStep.mobileHint')}</p>}
                </div>
                <Input
                  id="guardianEmail"
                  type="email"
                  label={t('step2.email')}
                  value={guardianData.guardianEmail || ''}
                  onChange={(e) => {
                    setGuardianData({ guardianEmail: e.target.value })
                    if (emailError) setEmailError(undefined)
                  }}
                  onBlur={() => {
                    setEmailError(!email || isValidEmail(email) ? undefined : t('step2.emailInvalid'))
                  }}
                  error={emailError}
                />
                <Textarea
                  id="guardianAddress"
                  label={t('step4.guardianAddressLabel')}
                  placeholder={t('step4.guardianAddressPlaceholder')}
                  value={guardianData.guardianAddress}
                  onChange={(e) => setGuardianData({ guardianAddress: e.target.value })}
                  rows={3}
                />

                {/* Certificato di nomina (optional) */}
                <div>
                  <label className="label-text">{t('step4.guardianDocumentLabel')}</label>
                  <p className="text-small text-muted-foreground mb-2">{t('step4.guardianDocumentHint')}</p>
                  {guardianData.guardianDocumentFilename ? (
                    <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <FileText className="w-5 h-5 text-green-600" />
                      <span className="flex-1 text-small text-green-700 truncate">
                        {guardianData.guardianDocumentFilename}
                      </span>
                      <button
                        type="button"
                        onClick={() => setGuardianData({ guardianDocumentFilename: '' })}
                        className="p-1 hover:bg-green-100 rounded"
                      >
                        <X className="w-4 h-4 text-green-600" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary hover:bg-primary/5 transition-all">
                      <Upload className="w-5 h-5 text-muted-foreground" />
                      <span className="text-small text-muted-foreground">
                        {isUploading ? t('common.uploadingFile') : t('common.uploadPdf')}
                      </span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={handleDocumentUpload}
                        className="hidden"
                        disabled={isUploading}
                      />
                    </label>
                  )}
                </div>

                {/* Minor with a parent: who signs the consent — both parents, or one in Arne's three cases */}
                {needsSignatureChoice && (
                  <fieldset className="space-y-3 pt-4 border-t border-border">
                    <legend className="text-body font-medium text-foreground mb-1">{t('guardianStep.signaturesLabel')} *</legend>
                    {(['both', 'single'] as const).map((v) => (
                      <label
                        key={v}
                        className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          guardianData.guardianSignatures === v ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="guardianSignatures"
                          value={v}
                          checked={guardianData.guardianSignatures === v}
                          onChange={() => setGuardianData({ guardianSignatures: v })}
                          className="mt-1"
                        />
                        <span className="text-body">{t(v === 'both' ? 'guardianStep.signaturesBoth' : 'guardianStep.signaturesSingle')}</span>
                      </label>
                    ))}

                    {guardianData.guardianSignatures === 'both' && (
                      <div className="space-y-4 pt-2">
                        <h3 className="text-body font-medium text-foreground">{t('guardianStep.secondParentTitle')}</h3>
                        <Input
                          id="guardian2FirstName"
                          type="text"
                          label={`${t('step2.firstName')} *`}
                          value={guardianData.guardian2FirstName || ''}
                          onChange={(e) => setGuardianData({ guardian2FirstName: e.target.value })}
                        />
                        <Input
                          id="guardian2LastName"
                          type="text"
                          label={`${t('step2.lastName')} *`}
                          value={guardianData.guardian2LastName || ''}
                          onChange={(e) => setGuardianData({ guardian2LastName: e.target.value })}
                        />
                        <div>
                          <Input
                            id="guardian2Phone"
                            type="tel"
                            label={`${t('guardianStep.mobileLabel')} *`}
                            placeholder={t('step2.phonePlaceholder')}
                            value={guardianData.guardian2Phone || ''}
                            onChange={(e) => {
                              setGuardianData({ guardian2Phone: e.target.value })
                              if (phone2Error) setPhone2Error(undefined)
                            }}
                            onBlur={() => setPhone2Error(phoneOk(phone2) ? undefined : phoneMessage(phone2))}
                            error={phone2Error}
                          />
                          <p className="text-small text-muted-foreground mt-1">{t('guardianStep.mobileHint')}</p>
                        </div>
                        <Input
                          id="guardian2Email"
                          type="email"
                          label={t('step2.email')}
                          value={guardianData.guardian2Email || ''}
                          onChange={(e) => {
                            setGuardianData({ guardian2Email: e.target.value })
                            if (email2Error) setEmail2Error(undefined)
                          }}
                          onBlur={() => setEmail2Error(!email2 || isValidEmail(email2) ? undefined : t('step2.emailInvalid'))}
                          error={email2Error}
                        />
                      </div>
                    )}

                    {guardianData.guardianSignatures === 'single' && (
                      <div className="pt-2">
                        <label htmlFor="guardianSingleSignatureReason" className="block text-body font-medium text-foreground mb-1.5">
                          {t('guardianStep.singleReasonLabel')} *
                        </label>
                        <select
                          id="guardianSingleSignatureReason"
                          value={guardianData.guardianSingleSignatureReason || ''}
                          onChange={(e) => setGuardianData({ guardianSingleSignatureReason: e.target.value })}
                          className="w-full px-4 py-3 text-body border border-border rounded-input outline-none bg-white focus:border-primary"
                        >
                          <option value="">{t('guardianStep.singleReasonPlaceholder')}</option>
                          {SINGLE_SIGNATURE_REASONS.map((r) => (
                            <option key={r} value={r}>{t(`guardianStep.singleReasons.${r}`)}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </fieldset>
                )}
              </>
            )}
          </div>
        )}

        <FormNavigation onBack={onBack} nextDisabled={!isValid || isUploading} />
      </form>
    </div>
  )
}
